import { Trans, useTranslation } from '@gamiphy/react-i18n';
import { delay } from '@gamiphy/utils/general';
import {
  Box, Button, FormHelperText, Typography,
} from '@mui/material';
import { isAxiosError } from 'axios';
import { observer } from 'mobx-react';
import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useOtpResendCountdown } from './hooks';
import OtpFields from './OtpFields';
import { OtpFormValues } from './types';

const OtpForm: React.FC<{
  phone: string,
  onEditPhone: () => void,
  onDone?: () => void,
  height: number,
  fontSize: number,
  buttonsDirection: 'row' | 'column',
  verifyPhone: (phone: string, otp: string) => Promise<void>,
  fetchDiscounts: () => Promise<void>,
  resendOtp: (phone: string) => Promise<void>,
  fullWidthButtons: boolean,
  ctaLabel?: string,
}> = ({
  phone,
  onEditPhone,
  onDone,
  height,
  fontSize,
  buttonsDirection,
  verifyPhone,
  fetchDiscounts,
  resendOtp,
  fullWidthButtons,
  ctaLabel,
}) => {
  const { t } = useTranslation();
  const [resending, setResending] = React.useState(false);

  const {
    countdown,
    secondsToResend,
    setSecondsToResend,
  } = useOtpResendCountdown();

  const {
    setValue,
    control,
    formState,
    setError,
    handleSubmit,
    reset,
  } = useForm<OtpFormValues>({
    defaultValues: {
      otpDigits: Array.from({ length: 6 }, () => ({ value: '' })),
    },
  });

  const otpValues = useWatch({
    control,
    name: 'otpDigits',
  });

  const onSubmit = React.useCallback(handleSubmit(async (data) => {
    try {
      const otp = data.otpDigits.map((digitObj) => digitObj.value).join('');

      await verifyPhone(phone, otp);
      await delay(1000);
      await fetchDiscounts();
      onDone?.();
    } catch (e) {
      if (isAxiosError(e)) {
        switch (e.response?.status) {
          case 400:
            setError('root.invalidOtp', { message: t('coreloyalty.cashback.pin.invalid', 'Invalid PIN code') });
            break;
          case 409:
            setError('root.userAlreadyExists', { message: t('coreloyalty.cashback.phone.already.exists', 'Phone already exists') });
            break;
          default:
            setError('root.serverError', { message: t('promo.warning', 'Something went wrong') });
        }
      }
    }
  }), [
    onDone,
  ]);

  React.useEffect(() => {
    if (otpValues.every((digitObj) => digitObj.value && digitObj.value.trim() !== '')) {
      void onSubmit();
    }
  }, [otpValues, onSubmit]);

  const resend = React.useCallback(async () => {
    reset();
    setResending(true);
    await resendOtp(phone);
    setResending(false);
    setSecondsToResend(119);
  }, [phone]);

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Box>
        <OtpFields
          control={control}
          setValue={setValue}
          fontSize={fontSize}
          height={height}
        />
        <Typography fontSize={16} fontWeight={700} sx={{ opacity: 0.5 }} mt={0.5}>
          {secondsToResend ? (
            <Trans
              i18nKey="coreloyalty.cashback.pin.resend.countdown"
              defaults="Resend code in {{countdown}}"
              values={{
                countdown,
              }}
            />
          ) : (
            <Button
              variant="text"
              onClick={resend}
              disabled={resending}
              sx={{
                p: 0,
                fontSize: 16,
                fontWeight: 700,
                textDecoration: 'underline',
              }}
            >
              <Trans
                i18nKey="coreloyalty.cashback.pin.resend"
                defaults="Resend"
              />
            </Button>
          )}
        </Typography>
        {formState.errors.root?.invalidOtp && (
          <FormHelperText error sx={{ fontSize: 14 }}>
            {formState.errors.root?.invalidOtp.message}
          </FormHelperText>
        )}
        {formState.errors.root?.userAlreadyExists && (
          <FormHelperText error sx={{ fontSize: 14 }}>
            {formState.errors.root?.userAlreadyExists.message}
          </FormHelperText>
        )}
      </Box>
      <Box display="flex" flexDirection={buttonsDirection} gap={1} mt={1.5}>
        <Button
          variant="outlined"
          fullWidth={fullWidthButtons}
          color="inherit"
          onClick={onEditPhone}
          disabled={secondsToResend > 0}
          sx={{
            order: buttonsDirection === 'row' ? 0 : 1,
            fontSize: 16,
            borderColor: '#D0D5DD',
          }}
        >
          <Trans
            i18nKey="cashbackPopup.number.edit"
            defaults="Edit my number"
          />
        </Button>
        <Button
          variant="contained"
          fullWidth={fullWidthButtons}
          type="submit"
          disabled={formState.isSubmitting}
          sx={{
            order: buttonsDirection === 'row' ? 1 : 0,
            fontSize: 16,
          }}
        >
          {ctaLabel ?? (
          <Trans
            i18nKey="coreloyalty.cashback.pin.enter.cta"
            defaults="Enter"
          />
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default observer(OtpForm);
